// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LinesHeader, { LinesHeaderStyles } from "@guestsupply/App/Components/Cart/LinesHeader";
import InvoiceDetailsLineCard, {
    InvoiceDetailsLineCardStyles,
} from "@guestsupply/Overrides/Widgets/InvoiceDetails/InvoiceDetailsLineCard";
import { groupSortBy } from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useContext } from "react";
import { css } from "styled-components";

export interface InvoiceDetailsSummaryTableStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: GridContainerProps;
    titleItem?: GridItemProps;
    title?: TypographyProps;
    linesItem?: GridItemProps;
    linesInnerContainer?: GridContainerProps;
    lineItem?: GridItemProps;
    lineStyles?: InvoiceDetailsLineCardStyles;
    // XNG-Changes
    lineHeader?: LinesHeaderStyles;
    groupTitle?: InjectableCss;
    groupWrapper?: InjectableCss;
}

export const summaryTableStyles: InvoiceDetailsSummaryTableStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    titleItem: {
        width: 12,
        css: css`
            padding-bottom: 10px;
        `,
    },
    title: {
        weight: "bold",
        size: "18px",
        as: "h2",
        css: css`
            @media print {
                font-size: 15px;
            }
            width: 100%;
            padding: 15px 0;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    linesItem: {
        width: 12,
    },
    lineItem: {
        width: 12,
        css: css`
            padding-top: 20px;
            border-bottom: 1px solid ${getColor("common.border")};
            &:first-child {
                padding-top: 10px;
            }
        `,
    },
};

const styles = summaryTableStyles;

const InvoiceDetailsSummaryTable = () => {
    const invoiceState = useContext(InvoiceStateContext);
    if (invoiceState.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }

    const invoice = invoiceState.value;

    if (!invoice || !invoice.invoiceLines || invoice.invoiceLines.length === 0) {
        return null;
    }

    const showSurcharges = invoice.otherCharges > 0;
    const invoiceLineGroups = groupSortBy(invoice.invoiceLines, "pCeCom", "catSort", "prodSort");

    // XNG-Changes (LineHeader, moving Invoice Summary to total summary box)
    return (
        <StyledWrapper {...styles.container} data-test-selector="invoiceDetail_summaryTable">
            <StyledWrapper {...styles.linesInnerContainer}>
                <LinesHeader showSurcharges={showSurcharges} extendedStyles={showSurcharges ? styles.lineHeader : {}} />
                {invoiceLineGroups.map(invoiceLine => (
                    <>
                        <StyledWrapper {...styles.groupTitle}>
                            {invoiceLine[0].properties.pCeCom || "Other"}
                        </StyledWrapper>
                        <StyledWrapper {...styles.groupWrapper}>
                            {invoiceLine.map(invoiceLine => (
                                <InvoiceDetailsLineCard
                                    key={invoiceLine.id}
                                    invoiceLine={invoiceLine}
                                    extendedStyles={styles.lineStyles}
                                    data-test-selector={`invoiceDetails_invoiceLine_${invoiceLine.id}`}
                                    showSurcharges={showSurcharges}
                                />
                            ))}
                        </StyledWrapper>
                    </>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: InvoiceDetailsSummaryTable,
    definition: {
        group: "Invoice Details",
        displayName: "Summary Table",
        allowedContexts: ["InvoiceDetailsPage"],
    },
};

export default widgetModule;
